import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import { useSocket } from '../contexts/SocketContext';
import { useVoice } from '../contexts/VoiceContext';
import { useTranslation } from 'react-i18next';
import { ServerRail } from '../components/ServerRail';
import { ChannelSidebar } from '../components/ChannelSidebar';
import { ChatArea } from '../components/ChatArea';
import { MemberSidebar } from '../components/MemberSidebar';
import { UserPanel } from '../components/UserPanel';
import { DMView } from '../components/DMView';
import { FriendsView } from '../components/FriendsView';
import { UserSettings } from '../components/UserSettings';
import { ServerSettings } from '../components/ServerSettings';
import { VideoGrid } from '../components/VideoGrid';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuCheckboxItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Loader2, Menu, X, Users, Volume2, Gamepad2, UserPlus, Mic, MicOff, Headphones, HeadphoneOff, MonitorUp, MonitorOff, Video, VideoOff, ChevronDown, MoreHorizontal, PhoneOff } from 'lucide-react';

export default function MainApp() {
  const { user, loading: authLoading } = useAuth();
  const { view, currentChannel, currentDm, loading: appLoading, setChannels, selectChannel } = useApp();
  const { connected, on } = useSocket();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showServerSettings, setShowServerSettings] = useState(false);

  useEffect(() => {
    if (!connected || !on) return;
    const unsubCreate = on('CHANNEL_CREATE', (event) => {
      setChannels(prev => [...prev, event.data]);
    });
    const unsubDelete = on('CHANNEL_DELETE', (event) => {
      setChannels(prev => prev.filter(c => c.channel_id !== event.data.channel_id));
    });
    const unsubUpdate = on('CHANNEL_UPDATE', (event) => {
      setChannels(prev => prev.map(c => c.channel_id === event.data.channel_id ? event.data : c));
    });
    return () => { unsubCreate?.(); unsubDelete?.(); unsubUpdate?.(); };
  }, [connected, on, setChannels]);

  if (authLoading || appLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-[#0b0d10]" data-testid="app-loading">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
          <span className="text-sm text-zinc-400">Loading World Gaming...</span>
        </div>
      </div>
    );
  }

  if (showSettings) return <UserSettings onClose={() => setShowSettings(false)} />;
  if (showServerSettings) return <ServerSettings onClose={() => setShowServerSettings(false)} />;

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-[#0b0d10] text-zinc-100" data-testid="main-app">
      <button className="lg:hidden fixed top-3 left-3 z-50 p-2 rounded-lg bg-[#1a1d23] hover:bg-[#22252d] transition-colors"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
        {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {mobileMenuOpen && <div className="lg:hidden fixed inset-0 bg-black/50 z-30" onClick={() => setMobileMenuOpen(false)} />}

      <div className={`fixed lg:relative z-40 h-full ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} transition-transform duration-200`}>
        <ServerRail />
      </div>

      {view === 'server' && (
        <div className={`fixed lg:relative z-40 h-full left-[72px] lg:left-0 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-[312px] lg:translate-x-0'} transition-transform duration-200`}>
          <div className="flex flex-col h-full">
            <ChannelSidebar onOpenServerSettings={() => setShowServerSettings(true)} />
            <UserPanel onOpenSettings={() => setShowSettings(true)} />
          </div>
        </div>
      )}

      {(view === 'dms' || view === 'friends') && (
        <div className={`fixed lg:relative z-40 h-full left-[72px] lg:left-0 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-[312px] lg:translate-x-0'} transition-transform duration-200`}>
          <div className="flex flex-col h-full w-60 bg-[#0e1015] border-r border-white/5">
            <DMSidebar />
            <UserPanel onOpenSettings={() => setShowSettings(true)} />
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {view === 'server' && currentChannel ? (
          currentChannel.channel_type === 'voice' ? (
            <VoiceChannelView />
          ) : (
            <ChatArea />
          )
        ) : view === 'server' ? (
          <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">
            Select a channel to start chatting
          </div>
        ) : view === 'friends' ? (
          <FriendsView />
        ) : view === 'dms' && currentDm ? (
          <DMView />
        ) : (
          <FriendsView />
        )}
      </div>

      {view === 'server' && (
        <div className="hidden lg:flex">
          <MemberSidebar />
        </div>
      )}
    </div>
  );
}

const VoiceChannelView = () => {
  const { currentChannel } = useApp();
  const { voiceChannel, joinVoice } = useVoice();

  if (!currentChannel) return null;

  const isConnected = voiceChannel === currentChannel.channel_id;

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-[#0b0d10]" data-testid="voice-channel-view">
      <div className="h-12 px-4 flex items-center gap-2 border-b border-white/5" data-testid="voice-channel-header">
        <Volume2 className="h-4 w-4 text-zinc-500" />
        <span className="font-semibold text-sm text-white flex-1 truncate">{currentChannel.name}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full ${isConnected ? 'bg-green-500/10 text-green-400' : 'bg-zinc-800 text-zinc-400'}`} data-testid="voice-connected-status">
          {isConnected ? 'Conectat' : 'Deconectat'}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {isConnected ? (
          <>
            <div className="space-y-4" data-testid="voice-panels">
              <VideoGrid />
              <div className="bg-[#12141a] border border-white/10 rounded-2xl p-6 min-h-[240px]" data-testid="voice-activity-panel">
                <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-wider">
                  <Gamepad2 className="h-4 w-4" />
                  <span>World Gaming Voice</span>
                </div>
                <h3 className="text-xl font-semibold text-white mt-3">Canalul este activ</h3>
                <p className="text-sm text-zinc-400 mt-2">
                  Vorbește cu prietenii, pornește camera sau partajează ecranul direct din bara de voce.
                </p>
                <div className="flex flex-wrap gap-2 mt-6">
                  <Button variant="outline" disabled className="border-white/10 text-zinc-400" data-testid="voice-invite-btn">
                    <UserPlus className="h-4 w-4 mr-2" />Invită în canalul de voce
                  </Button>
                  <Button variant="outline" disabled className="border-white/10 text-zinc-400" data-testid="voice-activity-btn">
                    <Gamepad2 className="h-4 w-4 mr-2" />Alege o activitate
                  </Button>
                </div>
              </div>
            </div>
            <VoiceControlsBar />
          </>
        ) : (
          <div className="max-w-xl w-full mx-auto bg-[#12141a] border border-white/10 rounded-2xl p-8 text-center" data-testid="voice-disconnected-card">
            <div className="h-12 w-12 rounded-full bg-cyan-500/10 flex items-center justify-center mx-auto">
              <Volume2 className="h-6 w-6 text-cyan-400" />
            </div>
            <h3 className="text-xl font-semibold text-white mt-4">Nu ești conectat</h3>
            <p className="text-sm text-zinc-400 mt-2">
              Apasă butonul de mai jos ca să intri în canalul de voce.
            </p>
            <Button onClick={() => joinVoice(currentChannel.channel_id)} className="bg-cyan-600 hover:bg-cyan-700 text-white mt-4" data-testid="voice-connect-btn">
              Conectează-te
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const VoiceControlsBar = () => {
  const {
    muted,
    deafened,
    cameraOn,
    screenSharing,
    pushToTalk,
    toggleMute,
    toggleDeafen,
    toggleCamera,
    toggleScreenShare,
    togglePushToTalk,
    leaveVoice,
  } = useVoice();
  const [showGrid, setShowGrid] = useState(true);
  const [showSelfPreview, setShowSelfPreview] = useState(true);

  return (
    <div className="sticky bottom-6 flex justify-center" data-testid="voice-controls-wrapper">
      <div className="flex items-center gap-2 bg-[#0f1117] border border-white/10 rounded-2xl px-3 py-2 shadow-xl" data-testid="voice-controls-bar">
        <div className="flex items-center bg-[#1a1d23] rounded-xl overflow-hidden border border-white/10">
          <button
            onClick={toggleMute}
            className={`px-3 py-2 text-sm ${muted ? 'text-red-400 bg-red-500/10' : 'text-zinc-300 hover:text-white hover:bg-white/5'}`}
            data-testid="voice-control-mute-btn"
            title={muted ? 'Unmute' : 'Mute'}
          >
            {muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="px-2 py-2 text-zinc-400 hover:text-white hover:bg-white/5"
                data-testid="voice-control-mic-menu-btn"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#12141a] border-white/10 text-white" side="top">
              <DropdownMenuItem data-testid="voice-mic-input-menu">Dispozitiv de intrare</DropdownMenuItem>
              <DropdownMenuItem data-testid="voice-mic-profile-menu">Profil de intrare</DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem data-testid="voice-settings-menu">Setări voce</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <button
          onClick={toggleDeafen}
          className={`px-3 py-2 rounded-xl border border-white/10 ${deafened ? 'text-red-400 bg-red-500/10' : 'text-zinc-300 hover:text-white hover:bg-white/5'}`}
          data-testid="voice-control-deafen-btn"
          title={deafened ? 'Undeafen' : 'Deafen'}
        >
          {deafened ? <HeadphoneOff className="h-4 w-4" /> : <Headphones className="h-4 w-4" />}
        </button>

        <div className="flex items-center bg-[#1a1d23] rounded-xl overflow-hidden border border-white/10">
          <button
            onClick={toggleCamera}
            className={`px-3 py-2 ${cameraOn ? 'text-cyan-300 bg-cyan-500/10' : 'text-zinc-300 hover:text-white hover:bg-white/5'}`}
            data-testid="voice-control-camera-btn"
            title={cameraOn ? 'Oprește camera' : 'Pornește camera'}
          >
            {cameraOn ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="px-2 py-2 text-zinc-400 hover:text-white hover:bg-white/5" data-testid="voice-control-camera-menu-btn">
                <ChevronDown className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#12141a] border-white/10 text-white" side="top">
              <DropdownMenuItem data-testid="voice-camera-device-menu">Cameră</DropdownMenuItem>
              <DropdownMenuItem data-testid="voice-camera-preview-menu">Previzualizează camera</DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem data-testid="voice-video-settings-menu">Setări video</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <button
          onClick={toggleScreenShare}
          className={`px-3 py-2 rounded-xl border border-white/10 ${screenSharing ? 'text-cyan-300 bg-cyan-500/10' : 'text-zinc-300 hover:text-white hover:bg-white/5'}`}
          data-testid="voice-control-screen-btn"
          title={screenSharing ? 'Oprește partajarea' : 'Partajează ecran'}
        >
          {screenSharing ? <MonitorUp className="h-4 w-4" /> : <MonitorOff className="h-4 w-4" />}
        </button>

        <button
          className="px-3 py-2 rounded-xl border border-white/10 text-zinc-300 hover:text-white hover:bg-white/5"
          data-testid="voice-control-activity-btn"
          title="Alege o activitate"
        >
          <Gamepad2 className="h-4 w-4" />
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="px-3 py-2 rounded-xl border border-white/10 text-zinc-300 hover:text-white hover:bg-white/5" data-testid="voice-control-more-btn">
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-[#12141a] border-white/10 text-white" side="top">
            <DropdownMenuCheckboxItem checked={showGrid} onCheckedChange={setShowGrid} data-testid="voice-more-grid-toggle">
              Aspect grilă
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem checked={showSelfPreview} onCheckedChange={setShowSelfPreview} data-testid="voice-more-self-preview-toggle">
              Arată previzualizarea mea
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem checked={pushToTalk} onCheckedChange={togglePushToTalk} data-testid="voice-more-ptt-toggle">
              Push-to-talk (V)
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem data-testid="voice-more-settings">Setări video și de voce</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <button
          onClick={leaveVoice}
          className="px-3 py-2 rounded-xl bg-red-500/90 text-white hover:bg-red-500"
          data-testid="voice-control-disconnect-btn"
          title="Disconnect"
        >
          <PhoneOff className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const DMSidebar = () => {
  const { dms, currentDm, selectDm, switchToFriends, view } = useApp();
  const { t } = useTranslation();

  return (
    <>
      <div className="h-12 px-4 flex items-center border-b border-white/5">
        <span className="text-sm font-semibold text-white">Messages</span>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-thin px-2 py-2">
        <button onClick={switchToFriends} data-testid="sidebar-friends-btn"
          className={`flex items-center gap-2.5 w-full px-3 py-2 rounded-lg mb-1 transition-colors ${
            view === 'friends' ? 'bg-white/10 text-white' : 'text-zinc-400 hover:text-white hover:bg-white/5'
          }`}>
          <Users className="h-5 w-5" />
          <span className="text-sm font-medium">{t('app.friends')}</span>
        </button>

        <p className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold px-2 mt-3 mb-1">
          {t('app.directMessages')}
        </p>

        {dms.map(dm => (
          <button key={dm.dm_id} onClick={() => selectDm(dm)} data-testid={`dm-sidebar-${dm.dm_id}`}
            className={`flex items-center gap-2.5 w-full px-2 py-1.5 rounded-lg mb-0.5 transition-colors ${
              currentDm?.dm_id === dm.dm_id ? 'bg-white/10 text-white' : 'text-zinc-400 hover:text-white hover:bg-white/5'
            }`}>
            <Avatar className="h-8 w-8">
              <AvatarImage src={dm.user?.avatar} />
              <AvatarFallback className="bg-cyan-600/20 text-cyan-300 text-xs">
                {(dm.user?.display_name || dm.user?.username || '?')[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <span className="text-[13px] truncate">{dm.user?.display_name || dm.user?.username}</span>
          </button>
        ))}
      </div>
    </>
  );
};
